import { useState, useRef, useCallback, useEffect } from "react";
import Confetti from "canvas-confetti";
import { useSettings } from "@/hooks/useSettings";
import { useGameStats } from "@/hooks/useGameStats";

interface HandState {
  angle: number;
  value: number;
}

interface GameTarget {
  hour: number;
  minute: number;
  display: string;
}

const generateTargetTime = (difficulty: string): GameTarget => {
  const hour = Math.floor(Math.random() * 12) + 1;
  let minute: number;

  switch (difficulty) {
    case "easy":
      minute = 0;
      break;
    case "medium":
      minute = Math.floor(Math.random() * 4) * 15;
      break;
    case "hard":
    default:
      minute = Math.floor(Math.random() * 60);
      break;
  }

  const displayHour = hour;
  const minuteStr = String(minute).padStart(2, "0");
  return {
    hour,
    minute,
    display: `${displayHour}h${minuteStr}`,
  };
};

const numberToWords = (n: number): string => {
  const units = [
    "zéro", "une", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix",
    "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf",
  ];
  const tens = ["", "", "vingt", "trente", "quarante", "cinquante"];

  if (n < 20) return units[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;
  if (unit === 0) return tens[ten];
  if (ten === 2 && unit === 1) return "vingt et une";
  return `${tens[ten]}-${units[unit]}`;
};

const formatTimeText = (hour: number, minute: number): string => {
  const hourWords = [
    "minuit", "une", "deux", "trois", "quatre", "cinq", "six",
    "sept", "huit", "neuf", "dix", "onze", "douze",
  ];

  if (minute === 0) {
    return `${hourWords[hour]} heure${hour > 1 ? "s" : ""}`;
  }
  if (minute === 15) {
    return `${hourWords[hour]} heures et quart`;
  }
  if (minute === 30) {
    return `${hourWords[hour]} heures et demie`;
  }
  if (minute === 45) {
    const nextHour = hour === 12 ? 1 : hour + 1;
    return `${hourWords[nextHour]} heure${nextHour > 1 ? "s" : ""} moins le quart`;
  }
  return `${hourWords[hour]} heures et ${numberToWords(minute)} minutes`;
};

export default function GameSection() {
  const { difficulty, animationsEnabled, soundEnabled } = useSettings();
  const { addAttempt, addWin } = useGameStats();

  const [targetTime, setTargetTime] = useState<GameTarget>(() =>
    generateTargetTime(difficulty)
  );
  const [hourHand, setHourHand] = useState<HandState>({ angle: 300, value: 10 });
  const [minuteHand, setMinuteHand] = useState<HandState>({
    angle: 60,
    value: 10,
  });
  const [dragging, setDragging] = useState<"hour" | "minute" | null>(null);
  const [gameState, setGameState] = useState<"playing" | "won" | "lost">("playing");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackFading, setFeedbackFading] = useState(false);

  // Update target time when difficulty changes
  useEffect(() => {
    setTargetTime(generateTargetTime(difficulty));
    setHourHand({ angle: 300, value: 10 });
    setMinuteHand({ angle: 60, value: 10 });
    setGameState("playing");
    setShowFeedback(false);
  }, [difficulty]);

  // Auto-hide feedback after 5 seconds with fade out
  useEffect(() => {
    if (!showFeedback) return;
    const fadeTimer = setTimeout(() => {
      setFeedbackFading(true);
    }, 4000);
    const hideTimer = setTimeout(() => {
      setShowFeedback(false);
      setFeedbackFading(false);
    }, 5000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [showFeedback]);

  const clockRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (hand: "hour" | "minute", e: React.MouseEvent) => {
      e.preventDefault();
      if (gameState !== "playing") return;
      setDragging(hand);
    },
    [gameState]
  );

  const handleTouchStart = useCallback(
    (hand: "hour" | "minute", e: React.TouchEvent) => {
      e.preventDefault();
      if (gameState !== "playing") return;
      setDragging(hand);
    },
    [gameState]
  );

  const updateHandFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      if (!clockRef.current || !dragging) return;

      const rect = clockRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = clientX - centerX;
      const dy = clientY - centerY;
      let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      if (angle < 0) angle += 360;

      if (dragging === "hour") {
        const hourValue = Math.round(angle / 30);
        setHourHand({
          angle: hourValue * 30,
          value: hourValue === 0 ? 12 : hourValue,
        });
      } else {
        const minuteValue = Math.round(angle / 6);
        setMinuteHand({
          angle: minuteValue * 6,
          value: minuteValue === 60 ? 0 : minuteValue,
        });
      }
    },
    [dragging]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      updateHandFromEvent(e.clientX, e.clientY);
    },
    [dragging, updateHandFromEvent]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      if (!dragging) return;
      const touch = e.touches[0];
      updateHandFromEvent(touch.clientX, touch.clientY);
    },
    [dragging, updateHandFromEvent]
  );

  const handleEnd = useCallback(() => {
    setDragging(null);
  }, []);

  const checkAnswer = () => {
    addAttempt();
    const isCorrect =
      hourHand.value === targetTime.hour && minuteHand.value === targetTime.minute;

    if (isCorrect) {
      setGameState("won");
      addWin();
      setFeedbackMessage("Bravo ! Tu as trouvé !");
      setShowFeedback(true);

      if (animationsEnabled) {
        // Fire confetti
        const duration = 3000;
        const end = Date.now() + duration;

        const fireConfetti = () => {
          Confetti({
            particleCount: 5,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors: ["#E8913A", "#0D3328", "#F5F0EB", "#FFD700", "#FF6B6B"],
          });
          Confetti({
            particleCount: 5,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: ["#E8913A", "#0D3328", "#F5F0EB", "#FFD700", "#FF6B6B"],
          });

          if (Date.now() < end) {
            requestAnimationFrame(fireConfetti);
          }
        };
        fireConfetti();
      }
    } else {
      setGameState("lost");
      setFeedbackMessage("Pas tout à fait... Essaie encore !");
      setShowFeedback(true);
    }
  };

  const resetGame = () => {
    setTargetTime(generateTargetTime(difficulty));
    setHourHand({ angle: 300, value: 10 });
    setMinuteHand({ angle: 60, value: 10 });
    setGameState("playing");
    setShowFeedback(false);
  };

  // Generate tick marks
  const ticks = Array.from({ length: 60 }, (_, i) => {
    const isHour = i % 5 === 0;
    const isCardinal = i % 15 === 0;
    return { index: i, isHour, isCardinal };
  });

  return (
    <section
      id="jeu"
      className="relative min-h-screen bg-brand-light flex items-center justify-center py-20 overflow-hidden"
    >
      {/* Desk background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Top%20view%20of%20a%20warm%20wooden%20desk%20with%20a%20spiral%20notebook%2C%20colorful%20pencils%2C%20a%20small%20potted%20plant%2C%20soft%20morning%20light%20from%20window%2C%20cozy%20study%20atmosphere%2C%20warm%20beige%20and%20brown%20tones%2C%20shallow%20depth%20of%20field%2C%20professional%20photography%20style&width=1920&height=1080&seq=desk1&orientation=landscape"
          alt="Bureau"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        {/* Notebook card */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Notebook header */}
          <div className="bg-brand-primary px-6 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-accent flex items-center justify-center">
              <i className="ri-time-line text-white text-sm" />
            </div>
            <h2 className="font-display text-white text-lg font-semibold">
              Jeu du Temps
            </h2>
            <div className="ml-auto flex items-center gap-4 text-white/80 text-sm">
              <span>
                <i className="ri-bar-chart-line mr-1" />
                {difficulty === "easy"
                  ? "Facile"
                  : difficulty === "medium"
                  ? "Moyen"
                  : "Difficile"}
              </span>
            </div>
          </div>

          <div className="p-6 md:p-10">
            {/* Target time display */}
            <div className="text-center mb-8">
              <p className="text-brand-muted text-sm mb-2">
                Règle les aiguilles sur l'heure indiquée :
              </p>
              <div className="inline-block bg-brand-light px-8 py-4 rounded-xl">
                <p className="font-display text-3xl md:text-4xl font-bold text-brand-text">
                  {targetTime.display}
                </p>
                <p className="text-brand-muted text-base mt-2 font-medium">
                  {formatTimeText(targetTime.hour, targetTime.minute)}
                </p>
              </div>
            </div>

            {/* Game clock */}
            <div className="flex justify-center mb-8">
              <div
                ref={clockRef}
                className="relative w-[280px] h-[280px] md:w-[350px] md:h-[350px] rounded-full select-none touch-none"
                onMouseMove={handleMouseMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleEnd}
              >
                {/* Clock face */}
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    backgroundColor: "#f5f5f5",
                    boxShadow:
                      "inset 0 4px 20px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.12), inset 0 -2px 8px rgba(255,255,255,0.8)",
                  }}
                >
                  <div
                    className="absolute inset-3 rounded-full border-2"
                    style={{ borderColor: "rgba(0,0,0,0.06)" }}
                  />
                </div>

                {/* Tick marks */}
                {ticks.map((tick) => {
                  const angle = tick.index * 6;
                  const rad = (angle - 90) * (Math.PI / 180);
                  const radius = 50;
                  const x = 50 + radius * Math.cos(rad);
                  const y = 50 + radius * Math.sin(rad);

                  return (
                    <div
                      key={tick.index}
                      className="absolute"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                      }}
                    >
                      <div
                        className="rounded-full"
                        style={{
                          width: tick.isCardinal
                            ? "3px"
                            : tick.isHour
                            ? "2px"
                            : "1px",
                          height: tick.isCardinal
                            ? "18px"
                            : tick.isHour
                            ? "14px"
                            : "8px",
                          backgroundColor: "#333",
                          opacity: tick.isHour ? 1 : 0.5,
                        }}
                      />
                    </div>
                  );
                })}

                {/* Hour numbers */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => {
                  const angle = num * 30 - 90;
                  const rad = (angle * Math.PI) / 180;
                  const radius = 38;
                  const x = 50 + radius * Math.cos(rad);
                  const y = 50 + radius * Math.sin(rad);

                  return (
                    <div
                      key={num}
                      className="absolute font-display text-base md:text-lg font-bold"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: "translate(-50%, -50%)",
                        color: "#2D2D2D",
                      }}
                    >
                      {num}
                    </div>
                  );
                })}

                {/* Center dot */}
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full z-20"
                  style={{
                    backgroundColor: "#2D2D2D",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                />

                {/* Hour hand */}
                <div
                  className="absolute left-1/2 top-1/2 z-10 cursor-grab active:cursor-grabbing"
                  style={{
                    transform: `translate(-50%, -100%) rotate(${hourHand.angle}deg)`,
                    transformOrigin: "50% 100%",
                    height: "28%",
                    width: "6px",
                  }}
                  onMouseDown={(e) => handleMouseDown("hour", e)}
                  onTouchStart={(e) => handleTouchStart("hour", e)}
                >
                  <div
                    className="w-full h-full rounded-full"
                    style={{
                      backgroundColor: "#2D2D2D",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>

                {/* Minute hand */}
                <div
                  className="absolute left-1/2 top-1/2 z-10 cursor-grab active:cursor-grabbing"
                  style={{
                    transform: `translate(-50%, -100%) rotate(${minuteHand.angle}deg)`,
                    transformOrigin: "50% 100%",
                    height: "40%",
                    width: "4px",
                  }}
                  onMouseDown={(e) => handleMouseDown("minute", e)}
                  onTouchStart={(e) => handleTouchStart("minute", e)}
                >
                  <div
                    className="w-full h-full rounded-full"
                    style={{
                      backgroundColor: "#555",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Check button */}
            <div className="flex justify-center">
              {gameState === "playing" && (
                <button
                  onClick={checkAnswer}
                  className="px-8 py-4 bg-brand-accent text-white font-semibold rounded-xl hover:bg-brand-accent/90 transition-all cursor-pointer whitespace-nowrap text-lg"
                >
                  <i className="ri-check-double-line mr-2" />
                  As-tu trouvé ?
                </button>
              )}

              {gameState === "won" && (
                <div className="text-center">
                  <p className="font-display text-2xl font-bold text-green-600 mb-4">
                    <i className="ri-sparkling-line mr-2" />
                    Gagné !
                  </p>
                  <button
                    onClick={resetGame}
                    className="px-8 py-4 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2" />
                    Rejouer
                  </button>
                </div>
              )}

              {gameState === "lost" && (
                <div className="text-center">
                  <p className="font-display text-xl font-bold text-brand-accent mb-4">
                    <i className="ri-emotion-unhappy-line mr-2" />
                    Essaie encore !
                  </p>
                  <button
                    onClick={() => setGameState("playing")}
                    className="px-8 py-4 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-go-back-line mr-2" />
                    Réessayer
                  </button>
                </div>
              )}
            </div>

            {/* Feedback overlay */}
            {showFeedback && (
              <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                <div
                  className={`px-8 py-4 rounded-2xl text-white font-bold text-xl pointer-events-auto transition-all duration-1000 ${
                    gameState === "won"
                      ? "bg-green-500"
                      : "bg-brand-accent"
                  } ${
                    feedbackFading ? "opacity-0 scale-95" : "opacity-100 scale-100"
                  }`}
                  style={{
                    animation: feedbackFading ? undefined : "bounceIn 0.5s ease-out",
                  }}
                >
                  <i
                    className={`mr-2 ${
                      gameState === "won"
                        ? "ri-sparkling-line"
                        : "ri-emotion-unhappy-line"
                    }`}
                  />
                  {feedbackMessage}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}